import React, { useState, useEffect } from 'react';

const SpiderChart = ({ dimensions = [], maxScore = 100 }) => {
  const size = 600; // Larger canvas for labels
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = 100; // Smaller chart, more room for labels
  
  const numAxes = dimensions.length;
  const angleStep = numAxes > 0 ? (2 * Math.PI) / numAxes : 0;
  
  // Animation state
  const [animatedScores, setAnimatedScores] = useState([]);
  const [showContent, setShowContent] = useState(false);
  
  // Use JSON stringify to detect actual changes in dimensions
  const dimensionsKey = JSON.stringify(dimensions.map(d => d.score));
  
  useEffect(() => {
    if (dimensions.length === 0) return;
    
    // Initialize to 0
    setAnimatedScores(dimensions.map(() => 0));
    setShowContent(false);
    
    // Start animation after a brief delay
    const showTimer = setTimeout(() => {
      setShowContent(true);
    }, 100);
    
    // Animate to actual scores
    const animTimer = setTimeout(() => {
      setAnimatedScores(dimensions.map(d => d.score));
    }, 500);
    
    return () => {
      clearTimeout(showTimer);
      clearTimeout(animTimer);
    };
  }, [dimensionsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Early return after hooks
  if (numAxes < 3) return null;
  
  // Calculate point position on the radar
  const getPoint = (value, index) => {
    const angle = (index * angleStep) - (Math.PI / 2);
    const normalizedValue = (value / maxScore) * radius;
    return {
      x: centerX + normalizedValue * Math.cos(angle),
      y: centerY + normalizedValue * Math.sin(angle)
    };
  };
  
  // Generate grid lines (concentric polygons)
  const generateGridPolygon = (percentage) => {
    const points = [];
    for (let i = 0; i < numAxes; i++) {
      const angle = (i * angleStep) - (Math.PI / 2);
      const r = radius * percentage;
      points.push({
        x: centerX + r * Math.cos(angle),
        y: centerY + r * Math.sin(angle)
      });
    }
    return points.map(p => `${p.x},${p.y}`).join(' ');
  };
  
  // Generate data polygon with animated scores
  const currentScores = animatedScores.length === dimensions.length ? animatedScores : dimensions.map(() => 0);
  const dataPolygon = currentScores.map((score, i) => {
    const point = getPoint(score, i);
    return `${point.x},${point.y}`;
  }).join(' ');
  
  // Generate axis lines and labels - OPTIMIZED FOR 4 DIMENSIONS
  const axes = dimensions.map((dim, i) => {
    const angle = (i * angleStep) - (Math.PI / 2);
    const endX = centerX + radius * Math.cos(angle);
    const endY = centerY + radius * Math.sin(angle);
    
    // Larger distance for labels to ensure they're fully visible
    const labelDistance = radius + 100;
    let labelX = centerX + labelDistance * Math.cos(angle);
    let labelY = centerY + labelDistance * Math.sin(angle);
    
    // Position-specific adjustments for 4 dimensions
    let textAnchor = 'middle';
    let yAdjust = 0;
    let xAdjust = 0;
    
    if (numAxes === 4) {
      if (i === 0) { // Top - Strategie
        textAnchor = 'middle';
        yAdjust = -8;
      } else if (i === 1) { // Right - Governance
        textAnchor = 'start';
        xAdjust = 15;
        yAdjust = 0;
      } else if (i === 2) { // Bottom - Technologie & Data
        textAnchor = 'middle';
        yAdjust = 18;
      } else if (i === 3) { // Left - Mensen & Change
        textAnchor = 'end';
        xAdjust = -15;
        yAdjust = 0;
      }
    }
    
    const animScore = animatedScores[i] !== undefined ? animatedScores[i] : 0;
    
    return { 
      x1: centerX, 
      y1: centerY, 
      x2: endX, 
      y2: endY, 
      labelX: labelX + xAdjust, 
      labelY: labelY + yAdjust, 
      label: dim.dimension,
      score: dim.score,
      animatedScore: animScore,
      textAnchor,
      index: i
    };
  });
  
  // Grid levels (20%, 40%, 60%, 80%, 100%)
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  
  // Calculate average score
  const avgScore = Math.round(dimensions.reduce((sum, d) => sum + d.score, 0) / dimensions.length);
  const animatedAvgScore = animatedScores.length > 0 
    ? Math.round(animatedScores.reduce((sum, s) => sum + s, 0) / animatedScores.length) 
    : 0;
  
  return (
    <div className="relative flex flex-col items-center w-full overflow-visible">
      <svg 
        viewBox={`0 0 ${size} ${size}`} 
        className="w-full h-auto overflow-visible"
        style={{ maxWidth: '480px', minWidth: '320px', overflow: 'visible' }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Gradient and filter definitions */}
        <defs>
          <linearGradient id="spiderGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0.2" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="shadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.2"/>
          </filter>
        </defs>
        
        {/* Grid polygons */}
        {gridLevels.map((level, i) => (
          <polygon
            key={`grid-${level}`}
            points={generateGridPolygon(level)}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="1"
            className="transition-opacity duration-500"
            style={{ 
              opacity: showContent ? 1 : 0,
              transitionDelay: `${i * 50}ms`
            }}
          />
        ))}
        
        {/* Axis lines */}
        {axes.map((axis, i) => (
          <line
            key={`axis-${axis.x2}-${axis.y2}`}
            x1={axis.x1}
            y1={axis.y1}
            x2={axis.x2}
            y2={axis.y2}
            stroke="#d1d5db"
            strokeWidth="1"
            className="transition-opacity duration-500"
            style={{ 
              opacity: showContent ? 1 : 0,
              transitionDelay: `${100 + i * 50}ms`
            }}
          />
        ))}
        
        {/* Data polygon fill with animation */}
        <polygon
          points={dataPolygon}
          fill="url(#spiderGradient)"
          stroke="#2563eb"
          strokeWidth="2.5"
          filter="url(#glow)"
          className="transition-all duration-1000 ease-out"
        />
        
        {/* Data points */}
        {dimensions.map((dim, i) => {
          const animScore = currentScores[i] || 0;
          const point = getPoint(animScore, i);
          return (
            <circle
              key={`pt-${dim.dimension}`}
              cx={point.x}
              cy={point.y}
              r="6"
              fill="#2563eb"
              stroke="#fff"
              strokeWidth="2"
              filter="url(#shadow)"
              className="transition-all duration-1000"
            />
          );
        })}
        
        {/* Axis labels - ALWAYS VISIBLE */}
        {axes.map((axis) => (
          <g key={`label-${axis.index}`}>
            {/* Label background for readability - wider for side labels */}
            <rect
              x={axis.textAnchor === 'middle' ? axis.labelX - 70 : axis.textAnchor === 'start' ? axis.labelX - 5 : axis.labelX - 135}
              y={axis.labelY - 12}
              width="140"
              height="38"
              fill="white"
              rx="6"
              opacity="0.95"
              className="transition-opacity duration-500"
              style={{ 
                opacity: showContent ? 0.95 : 0,
                transitionDelay: `${200 + axis.index * 100}ms`
              }}
            />
            {/* Dimension name */}
            <text
              x={axis.labelX}
              y={axis.labelY}
              textAnchor={axis.textAnchor}
              dominantBaseline="middle"
              fontWeight="600"
              fill="#374151"
              fontSize="12"
              style={{ fontFamily: 'system-ui, sans-serif' }}
              className="transition-opacity duration-500"
            >
              {axis.label}
            </text>
            {/* Score with color coding */}
            <text
              x={axis.labelX}
              y={axis.labelY + 14}
              textAnchor={axis.textAnchor}
              dominantBaseline="middle"
              fontWeight="700"
              fill={axis.score >= 70 ? '#10b981' : axis.score >= 50 ? '#2563eb' : '#ef4444'}
              fontSize="14"
              style={{ fontFamily: 'system-ui, sans-serif' }}
              className="transition-all duration-1000"
            >
              {Math.round(axis.animatedScore)}%
            </text>
          </g>
        ))}
        
        {/* Center score circle */}
        <g className="transition-opacity duration-700" style={{ 
          opacity: showContent ? 1 : 0,
          transitionDelay: '300ms'
        }}>
          <circle 
            cx={centerX} 
            cy={centerY} 
            r="40" 
            fill="white" 
            stroke="#e5e7eb" 
            strokeWidth="2"
            filter="url(#shadow)"
          />
          <text
            x={centerX}
            y={centerY - 10}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#9ca3af"
            fontSize="10"
            fontWeight="600"
            letterSpacing="1"
          >
            SCORE
          </text>
          <text
            x={centerX}
            y={centerY + 12}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#374151"
            fontSize="26"
            fontWeight="700"
            className="transition-all duration-1000"
          >
            {animatedAvgScore}%
          </text>
        </g>
      </svg>
      
      {/* Legend */}
      <p className="text-xs text-gray-400 text-center mt-3 italic px-4">
        — Indicatieve visualisatie; scores zijn berekend op basis van quickscan-antwoorden
      </p>
    </div>
  );
};

export default SpiderChart;
